import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CollabCommitMergeResult } from '@common/collab/models/collab-commit-result.model';
import { WorkspacePullFileState } from '@common/workspace/models/workspace-pull-file-outcome.model';
import { WorkspacePullRequestBody } from '@common/workspace/models/workspace-pull-request-body.model';
import { WorkspacePullState } from '@common/workspace/models/workspace-pull-state.model';
import { CollabApiService } from '@portal-core/collab/services/collab-api.service';
import { CollabService } from '@portal-core/collab/services/collab.service';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { WorkspaceSessionService } from '@portal-core/workspaces/services/workspace-session.service';
import { BehaviorSubject, map, Observable, Subscription, timer } from 'rxjs';

/**
 * How long to keep the terminal state visible after a pull completes/fails before clearing it,
 * so the dialog/bar UI can display the outcome briefly even if the user lingers on the page.
 */
const TerminalStateLingerMS = 1500;

/**
 * Owns the lifecycle of a workspace pull operation across dialog open/close. Two responsibilities:
 *
 *  1. **Stateful progress feed.** Combines socket events from `WorkspaceSessionService` with a
 *     bootstrap query to `GET /pull/state` so subscribers always see the most recent snapshot —
 *     including when they subscribe mid-pull (re-opening the dialog) or right after a reconnect.
 *  2. **Background-friendly trigger.** `startPull` issues the HTTP request and tracks it here, not
 *     on the dialog component. That way the user can dismiss the dialog and the request keeps
 *     running; the result still drives the snackbar / conflict-resolution dialog.
 *
 * The HTTP response is treated as a fallback path for the result (in case the socket event missed
 * us); the `workspace.pull.completed` / `workspace.pull.failed` event normally arrives first.
 */
@Injectable({
  providedIn: 'root'
})
export class WorkspacePullProgressService {
  /**
   * Computes a single 0–100 progress percentage for a pull state. The per-file pipeline emits one
   * `onFileProcessed` per file completion so `filesProcessed / totalFiles` is already a smooth
   * monotone fraction — no per-phase weighting needed.
   *
   * - `null` / no state: 0
   * - `running`: `(filesProcessed / totalFiles) * 100`, capped at 100
   * - `completed`: 100
   * - `failed`: returns the last computed running progress (don't visually reset)
   */
  static aggregatePercent(state: WorkspacePullState | null): number {
    if (!state) return 0;
    if (state.status === 'completed') return 100;
    if (state.totalFiles <= 0) return 0;
    return Math.min(100, (state.filesProcessed / state.totalFiles) * 100);
  }

  /** Current pull state for the active workspace, or null when nothing is in flight or recent. */
  pullState$: BehaviorSubject<WorkspacePullState | null> = new BehaviorSubject<WorkspacePullState | null>(null);

  /** Tracks subscriptions wired up at construction time. */
  private socketSubscriptions: Subscription = new Subscription();

  /** The in-flight HTTP subscription for the current pull, if any. Survives dialog teardown. */
  private inFlightHttpSubscription: Subscription | null = null;

  /** Set when the terminal event arrives via socket so the HTTP completion can be ignored as a duplicate. */
  private resolvedThisPull: boolean = false;

  /**
   * The clientId we sent on our most recent `startPull` call. Compared against the pull state's
   * `startedByClientId` to decide whether *this* tab/window initiated the active pull. Used to gate
   * initiator-only side effects (success snackbar, conflict-resolution dialog, failure snackbar) so
   * other observers of the same workspace see the progress but not the initiator's UI.
   */
  private myInFlightClientId: string | null = null;

  constructor(
    private collabApiService: CollabApiService,
    private collabService: CollabService,
    private errorService: ErrorService,
    private snackBar: MatSnackBar,
    private workspaceSessionService: WorkspaceSessionService
  ) {
    // Stream socket events from the workspace session into our state observable. These can fire at
    // any time — including when no dialog is open — which is exactly the point.
    this.socketSubscriptions.add(this.workspaceSessionService.pullStarted$.subscribe(state => {
      this.resolvedThisPull = false;
      // The producer's `pull-started` broadcasts an empty `filePaths` because the producer doesn't
      // resolve the actual set — the consumer does that and broadcasts it via the subsequent
      // progress event. If we already have an optimistic state with `filePaths` populated for the
      // matching client/workspace, keep them so the dialog's row list doesn't flash to empty in the
      // brief window before the consumer's progress event arrives.
      const current = this.pullState$.value;
      const shouldKeepOptimisticPaths = current
        && current.workspaceId === state.workspaceId
        && current.filePaths.length > 0
        && state.filePaths.length === 0;
      this.pullState$.next(shouldKeepOptimisticPaths
        ? { ...state, filePaths: current.filePaths, totalFiles: current.filePaths.length }
        : state);
    }));

    this.socketSubscriptions.add(this.workspaceSessionService.pullProgress$.subscribe(state => {
      this.pullState$.next(state);
    }));

    this.socketSubscriptions.add(this.workspaceSessionService.pullCompleted$.subscribe(state => {
      this.pullState$.next(state);
      this.handleTerminalSuccess(state.result);
    }));

    this.socketSubscriptions.add(this.workspaceSessionService.pullFailed$.subscribe(state => {
      this.pullState$.next(state);
      this.handleTerminalFailure(state.error?.message);
    }));

    // Per-file completion deltas. Each one folds a single outcome into the local pullState's
    // `fileOutcomes` map so UIs binding to `fileOutcomes$` re-render the affected row without
    // waiting for the next aggregate-progress emit. We merge into the existing state rather than
    // overwriting it so the running totals (`filesProcessed` / `totalFiles`) are preserved.
    this.socketSubscriptions.add(this.workspaceSessionService.pullFileCompleted$.subscribe(({ filePath, fileOutcome }) => {
      const current = this.pullState$.value;
      if (!current) return;
      const next: WorkspacePullState = {
        ...current,
        fileOutcomes: { ...current.fileOutcomes, [filePath]: fileOutcome }
      };
      this.pullState$.next(next);
    }));

    // On every (re)connect, query the server for the current pull state so a freshly-loaded page
    // (or a reconnected socket) immediately reflects whatever is happening server-side. The socket
    // handlers above will continue to refine the state as new events arrive.
    this.socketSubscriptions.add(this.workspaceSessionService.connected$.subscribe(() => this.bootstrap()));
  }

  /**
   * Per-file terminal outcomes derived from the current pull state, suitable for driving per-row
   * status icons (checkmark / X / concurrent-edit) in the pull dialog. Updates on each file
   * completion via the socket delta and re-hydrates fully from `GET /pull/state` on reconnect.
   */
  fileOutcomes$: Observable<Record<string, WorkspacePullFileState>> = this.pullState$.pipe(
    map(state => state?.fileOutcomes ?? {})
  );

  /**
   * Triggers a pull. The HTTP request is owned by this service so it survives dialog dismissal.
   * Re-entrancy: if a pull is already in flight, the new request is dropped and existing state
   * remains the source of truth. The server enforces the same invariant via 409.
   *
   * @param body The pull request body.
   * @param expectedFilePaths The snapshot of file paths the caller expects to be pulled (typically
   *   the dialog's pre-pull list). Used to seed the optimistic state so the progress bar is
   *   determinate from frame zero AND the dialog's row list is non-empty before the consumer's
   *   authoritative `filePaths` arrives, avoiding a flash to an empty table during the brief
   *   window between the producer's `pull-started` event and the consumer's first progress event.
   */
  public startPull(body: WorkspacePullRequestBody, expectedFilePaths: string[] = []) {
    if (this.inFlightHttpSubscription && !this.inFlightHttpSubscription.closed) {
      return;
    }

    // Optimistically reflect "running" in the local state so the UI doesn't lag behind the user's
    // click while we wait for the server's first socket event.
    const optimistic: WorkspacePullState = {
      workspaceId: `${body.projectId}-${body.branchName}`,
      projectId: body.projectId,
      branchName: body.branchName,
      licenseId: body.licenseId,
      status: 'running',
      // Optimistic state mirrors what the server's `tryStartAsync` will broadcast — the
      // 'preparing' phase covers the silent window between "user clicked Pull" and "consumer
      // resolved the file list". The dialog renders this as "Computing changes…" instead of a
      // "Pulling…" caption sitting on a 0% bar.
      phase: 'preparing',
      totalFiles: expectedFilePaths.length,
      filesProcessed: 0,
      fileOutcomes: {},
      filePaths: expectedFilePaths,
      startedAt: new Date().toISOString(),
      startedByUserId: body.userId,
      startedByClientId: body.clientId,
      completedAt: null,
      result: null,
      error: null
    };
    // Set `myInFlightClientId` *before* the BehaviorSubject emits so synchronous subscribers
    // (e.g. the bar's "remote pull just started" pulse) see `isInitiatorOfActivePull() === true`
    // for this client and skip the observer-only UI on the very first frame.
    this.myInFlightClientId = body.clientId;
    this.resolvedThisPull = false;
    this.pullState$.next(optimistic);

    this.inFlightHttpSubscription = this.collabApiService.pullWorkspace$(body).subscribe({
      next: response => {
        this.inFlightHttpSubscription = null;
        // Two response shapes share one envelope:
        //  - status: 'running' (202) — the pull was queued for collab-pull. The terminal result
        //    will arrive via the `workspace.pull.completed` socket event; this HTTP body is
        //    informational only. Optimistic state is already in place, socket events take over.
        //  - status: 'completed' (200) — the server-side gate check found nothing to pull and
        //    short-circuited. No socket event will fire for this case, so drive terminal success
        //    now from the response body.
        const state = response?.Result?.pullState;
        if (state?.status === 'completed') {
          this.pullState$.next(state);
          this.handleTerminalSuccess(state.result);
        }
      },
      error: error => {
        this.inFlightHttpSubscription = null;

        // 409 means another client (or another tab of the same user) already has a pull running for
        // this workspace. The server returns the in-flight pull state in the response body so we can
        // bind to it and let the user observe progress instead of showing them a failure. We are not
        // the initiator in that case (it's the existing pull's owner), so subsequent terminal events
        // will skip our initiator-only side effects via `isInitiatorOfActivePull`.
        const conflictState = this.extractConflictPullState(error);
        if (conflictState) {
          this.pullState$.next(conflictState);
          // Forget our optimistic clientId — the active pull is owned by someone else.
          this.myInFlightClientId = null;
          return;
        }

        const messages = this.errorService.getErrorMessages(error);
        const message = messages?.length ? messages.join(' ') : 'Unable to pull the changes.';
        this.handleTerminalFailure(message);
      }
    });
  }

  /**
   * If the given HTTP error is a 409 from the workspace pull endpoint, extracts the in-flight pull
   * state the server included in the response body. Returns null otherwise.
   */
  private extractConflictPullState(error: unknown): WorkspacePullState | null {
    const httpError = error as { status?: number; error?: { Result?: { pullState?: WorkspacePullState } } };
    if (httpError?.status !== 409) {
      return null;
    }
    return httpError?.error?.Result?.pullState ?? null;
  }

  /**
   * Fetches the current pull state from the server. Called on connect / reconnect to recover
   * any pull that was already in flight before we started listening.
   */
  private bootstrap() {
    const projectId = this.workspaceSessionService.projectId;
    const branchName = this.workspaceSessionService.branchName;
    if (typeof projectId !== 'number' || !branchName) {
      return;
    }

    this.collabApiService.getWorkspacePullState$(projectId, branchName).subscribe({
      next: response => {
        const state = response?.Result?.pullState ?? null;
        // Only adopt the bootstrap snapshot if we don't already have a fresher in-memory state.
        // The socket subjects can race ahead of the HTTP query when the server has just emitted.
        const current = this.pullState$.value;
        if (state && (!current || (current.status !== 'running' && new Date(current.startedAt).getTime() < new Date(state.startedAt).getTime()))) {
          this.pullState$.next(state);
          // Bootstrap can load a terminal state (Redis keeps it under a short TTL after the pull
          // finishes). Without an explicit linger-clear, that terminal state would sit in
          // `pullState$` indefinitely — which means dialogs opened later would still see its
          // `fileOutcomes` and render stale per-row icons on what should be a fresh "ready to
          // pull" view. Schedule the same linger clear the socket-event terminal path uses so
          // the state self-cleans.
          if (state.status === 'completed' || state.status === 'failed') {
            this.scheduleTerminalStateLinger();
          }
        } else if (!state && current) {
          // Server says no pull is happening but we have a local state — clear it. This is the
          // "we missed the terminal event" recovery path: we lost socket connection mid-pull, the
          // server processed the terminal event + lingered it out to null while we were
          // disconnected, and now we have a stale optimistic running state that would otherwise
          // never go away. The server is authoritative about whether anything is in flight.
          this.pullState$.next(null);
          this.myInFlightClientId = null;
          this.resolvedThisPull = false;
        }
      },
      error: () => {
        // Bootstrap is best-effort; the next event will catch us up.
      }
    });
  }

  /**
   * Returns true when *this* tab/window initiated the active pull. Other tabs / users observing
   * the same workspace will see the progress (via socket events) but should not see initiator-only
   * UI like the success snackbar or the conflict-resolution dialog.
   *
   * Public so observer-only UI (e.g. the "remote pull just started" pulse on the bar) can branch on
   * it without needing direct access to `myInFlightClientId`.
   */
  public isInitiatorOfActivePull(): boolean {
    if (!this.myInFlightClientId) {
      return false;
    }
    const state = this.pullState$.value;
    return state?.startedByClientId === this.myInFlightClientId;
  }

  private handleTerminalSuccess(result: CollabCommitMergeResult | undefined) {
    // Both the socket event and the HTTP response can deliver the terminal result. The first
    // one wins; subsequent calls are no-ops so the user sees a single snackbar / single dialog.
    if (this.resolvedThisPull) {
      return;
    }
    this.resolvedThisPull = true;

    const hasUnresolvedConflicts = result?.conflictResolutionResult?.merged?.some(mergeResult => !mergeResult.success);

    // Success snackbar fires for everyone observing the workspace — the merge actually changed
    // shared state, so all viewers benefit from a brief "X files merged" summary regardless of
    // who initiated. Skipped when there are unresolved conflicts because the pull dialog's
    // post-pull summary (for the initiator) and the pull-changes bar (for everyone) carry the
    // more detailed signal in that case.
    if (!hasUnresolvedConflicts && result) {
      const { duration, message } = this.collabService.buildPullResultSnackBarMessage(result);
      if (message) {
        this.snackBar.open(message, null, {
          duration,
          panelClass: 'mc-snack-bar-panel-shrink'
        });
      }
    }

    // Subtract just the files this pull actually resolved (successful merges + soft-deletes +
    // pending-adds + no-ops) from the tracked remote-change set, leaving everything else alone.
    // Crucially this does NOT wholesale-replace the set: a fetch may have discovered new pullable
    // files mid-pull that aren't in this pull's snapshot, and a replace would silently drop them.
    // Unresolved merges (failed auto-merges) stay in the set so the bar keeps surfacing them
    // until the user handles them manually.
    const resolvedFilePaths = [
      ...(result?.conflictResolutionResult?.merged?.filter(m => m.success).map(m => m.filePath) ?? []),
      ...(result?.conflictResolutionResult?.deleted?.filter(m => m.success).map(m => m.filePath) ?? []),
      ...(result?.conflictResolutionResult?.noOps?.filter(m => m.success).map(m => m.filePath) ?? []),
      ...(result?.conflictResolutionResult?.pendingAdds?.filter(m => m.success).map(m => m.filePath) ?? [])
    ];
    this.workspaceSessionService.removeRemoteChanges(resolvedFilePaths);

    this.myInFlightClientId = null;

    this.scheduleTerminalStateLinger();
  }

  private handleTerminalFailure(message: string | undefined) {
    if (this.resolvedThisPull) {
      return;
    }
    this.resolvedThisPull = true;

    // Only the initiator gets the failure snackbar — other observers shouldn't see "Unable to
    // pull the changes" for someone else's failed pull (they didn't ask for it).
    if (this.isInitiatorOfActivePull()) {
      this.snackBar.open(message ?? 'Unable to pull the changes.', null, {
        duration: 5000,
        panelClass: 'mc-snack-bar-panel-shrink'
      });
    }

    this.myInFlightClientId = null;

    this.scheduleTerminalStateLinger();
  }

  /**
   * Drops `pullState$` back to `null` after a short delay if it's still in a terminal state.
   * Called from every code path that lands a terminal state on `pullState$` — the socket-event
   * handlers AND `bootstrap()` — so a stale "completed"/"failed" doesn't sit forever and bleed
   * into dialogs opened later. The "still terminal" check guards against a fresh running pull
   * having started during the linger window, in which case we leave the new state alone.
   */
  private scheduleTerminalStateLinger() {
    timer(TerminalStateLingerMS).subscribe(() => {
      const current = this.pullState$.value;
      if (current && current.status !== 'running') {
        this.pullState$.next(null);
      }
    });
  }
}
